// StepGeoVerify — Step 1.3 geofence renderer.
// Spec: docs/specs/location-tasks.md §8.4.3.

import React, { useState } from "react";
import { MapPin, Loader2, AlertTriangle } from "lucide-react";
import { useLiff } from "../../../liff/index.js";
import { apiFetch } from "../../../lib/api-client.js";
import type { WizardData } from "./types.js";

interface Props {
  data: WizardData;
  onComplete: () => void;
}

export default function StepGeoVerify({ data, onComplete }: Props): React.ReactElement {
  const { session } = useLiff();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{ distance: number; radius: number } | null>(null);

  const run = async (): Promise<void> => {
    if (!session?.session_token) return;
    if (!navigator.geolocation) {
      setError("此裝置不支援定位 API。");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 });
      });
      const r = await apiFetch(
        `/v1/patrols/submissions/${encodeURIComponent(data.submission.id)}/verify/geo`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        },
        { jwt: session.session_token },
      );
      const body = (await r.json().catch(() => ({}))) as { ok?: boolean; distance_m?: number; radius_m?: number; error?: string };
      if (!r.ok) {
        setError(body.error ?? `HTTP ${r.status}`);
        return;
      }
      if (typeof body.distance_m === "number" && typeof body.radius_m === "number") {
        setLastResult({ distance: body.distance_m, radius: body.radius_m });
      }
      if (!body.ok) {
        setError(`您距離此地點約 ${(body.distance_m ?? 0).toFixed(1)} m（允許 ${body.radius_m ?? 0} m 內）。`);
        return;
      }
      onComplete();
    } catch (e) {
      const msg = (e as GeolocationPositionError).message ?? (e as Error).message ?? "geo_failed";
      setError(`無法取得位置：${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <button
        onClick={run}
        disabled={busy}
        className="block w-full h-12 rounded-xl bg-accent text-text-inverse font-semibold text-sm disabled:bg-bg-raised disabled:text-text-muted"
      >
        {busy ? (
          <span className="inline-flex items-center justify-center gap-1"><Loader2 className="h-4 w-4 animate-spin" /> 取得位置中…</span>
        ) : (
          <span className="inline-flex items-center justify-center gap-1"><MapPin className="h-4 w-4" /> 開始定位</span>
        )}
      </button>
      {lastResult && (
        <p className="text-[10px] text-text-muted text-center">
          上次嘗試：{lastResult.distance.toFixed(1)} m / 允許 {lastResult.radius} m
        </p>
      )}
      {error && (
        <p className="text-xs text-danger inline-flex items-start gap-1">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />{error}
        </p>
      )}
    </div>
  );
}
